import { api } from '@/config/api';
import type { TemplateField } from '@/services/industry-configs.service';

export type FamilyKey = 'technology' | 'food' | 'real_estate' | 'travel';
export type BotMode = 'flow' | 'ai_chat' | 'off';
export type StageTerminal = 'active' | 'won' | 'lost';
export type DeptKind = 'sales' | 'tech' | 'ops' | 'support' | 'medical' | 'legal' | 'custom';

export interface CrmPageDef { key: string; label: string; group: string; general: boolean }
export interface CrmLabel { singular: string; plural: string }

export interface BlueprintRegistry {
  pages: CrmPageDef[];
  labels: Record<string, CrmLabel>;
  botModes: BotMode[];
}

export interface BlueprintStage { name: string; terminal: StageTerminal; probability: number; color?: string; rotDays?: number }
export interface BlueprintDepartment { name: string; description?: string; kind: DeptKind; color?: string; icon?: string }
export interface BlueprintService {
  name: string; description?: string; category?: string; durationMinutes: number; price?: number; bookable: boolean;
  kind?: 'product' | 'service';
  inCatalog?: boolean;
}
export interface BlueprintHours { days: number[]; start: string; end: string }

export interface Blueprint {
  familyKey: FamilyKey;
  name: string;
  description?: string;
  industries: string[];
  version: number;
  pages: Record<string, boolean>;
  labels: Record<string, CrmLabel>;
  fields: TemplateField[];
  pipeline: { name: string; stages: BlueprintStage[] };
  departments: BlueprintDepartment[];
  aiBotMode: BotMode;
  sampleServices: BlueprintService[];
  serviceHours: BlueprintHours;
  updatedBy?: string;
  updatedAt?: string;
  /** Live companies whose industry is in this family (list endpoint only). */
  companies?: number;
}

export type BlueprintUpdate = Partial<Omit<Blueprint, 'familyKey' | 'version' | 'updatedBy' | 'updatedAt' | 'companies'>>;

export interface BlueprintPlan {
  blueprint: { familyKey: FamilyKey; name: string; version: number } | null;
  industry: string | null;
  pages: { on: string[]; off: string[] };
  labels: Record<string, CrmLabel>;
  fields: { add: Array<{ entityType: string; name: string; key: string; kind: string; from: 'blueprint' | 'industry' }>; existing: number };
  pipeline: { name: string; action: 'create' | 'extend' | 'none'; addStages: string[]; makeDefault: boolean };
  departments: { add: string[]; existing: number };
  services: { add: Array<{ name: string; durationMinutes: number; price?: number }>; existing: number };
  catalog: { add: Array<{ name: string; kind: 'product' | 'service'; category?: string; price?: number; bookable: boolean }>; existing: number };
  botMode: { value: BotMode; willSet: boolean };
  launchDefaults?: { pending: boolean; recommendedModules: string[]; unavailableModules: string[]; templateNames: string[]; dashboardWidgets: string[] } | null;
  applied: { version: number; appliedAt: string } | null;
  upToDate: boolean;
  /** Set on the apply response only. */
  result?: { fields: number; stages: number; pipelines: number; departments: number; services: number; catalog: number };
}

export const blueprintsService = {
  registry: async (): Promise<BlueprintRegistry> => (await api.get('/super_admin/blueprints/registry')).data,
  list: async (): Promise<Blueprint[]> => (await api.get('/super_admin/blueprints')).data.items,
  get: async (familyKey: string): Promise<Blueprint> => (await api.get(`/super_admin/blueprints/${familyKey}`)).data,
  update: async (familyKey: string, p: BlueprintUpdate): Promise<Blueprint> =>
    (await api.put(`/super_admin/blueprints/${familyKey}`, p)).data,
  /** What a NEW company of this industry receives. */
  preview: async (industry: string): Promise<BlueprintPlan> =>
    (await api.get('/super_admin/blueprints/preview', { params: { industry } })).data,
  /** What the company's blueprint would add now. */
  companyPlan: async (companyUid: string): Promise<BlueprintPlan> =>
    (await api.get(`/super_admin/companies/${companyUid}/blueprint`)).data,
  apply: async (companyUid: string): Promise<BlueprintPlan> =>
    (await api.post(`/super_admin/companies/${companyUid}/blueprint/apply`)).data,
};

export const BOT_MODE_META: Record<BotMode, { label: string; hint: string }> = {
  ai_chat: { label: 'AI chat', hint: 'The AI answers WhatsApp messages using the business profile.' },
  flow: { label: 'Scripted flows', hint: 'Bot flows reply; nothing free-form.' },
  off: { label: 'Off', hint: 'No automatic replies — the team answers.' },
};

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
